import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import React, { useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import arrowIosForwardFill from '@iconify-icons/eva/arrow-ios-forward-fill';
import arrowIosDownwardFill from '@iconify-icons/eva/arrow-ios-downward-fill';
import { alpha, makeStyles } from '@material-ui/core/styles';
import _ from 'lodash';
import {
  Box,
  Collapse,
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { setIsClickNavbar } from 'src/redux/slices/consignmentStockReturnable';
import { resetGenerateProductCatalogue } from 'src/redux/slices/generateProductCatalogue';
import { openAsset } from "src/helpers/openAsset";
// ----------------------------------------------------------------------

const useStyles = makeStyles((theme) => ({
  root: {},
  listItem: {
    ...theme.typography.body2,
    height: 48,
    textTransform: 'capitalize',
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(2.5),
    color: theme.palette.text.secondary
  },
  subIcon: {
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:before': {
      width: 4,
      height: 4,
      content: "''",
      display: 'block',
      borderRadius: '50%',
      backgroundColor: theme.palette.text.disabled,
      transition: theme.transitions.create('transform')
    }
  },
  isActiveListItem: {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
    backgroundColor: alpha(
      theme.palette.primary.main,
      theme.palette.action.selectedOpacity
    ),
    '&:before': {
      top: 0,
      right: 0,
      width: 3,
      bottom: 0,
      content: "''",
      position: 'absolute',
      backgroundColor: theme.palette.primary.main
    }
  },
  isActiveListItemSub: {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightMedium,
    '& $subIcon:before': {
      transform: 'scale(2)',
      backgroundColor: theme.palette.primary.main
    }
  },
  customBadge: {
    backgroundColor: 'orange',
    color: 'white', 
    paddingBottom: '2px'
  },
  infoWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between', // Ensures space between elements, pushing badge to the right
    gap: theme.spacing(1), // Optional: adds space between elements
    width: '100%', // Ensures the container takes up full width
  },
  infoText: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    flexGrow: 1, // Makes the info text take up available space
    marginRight: theme.spacing(1), // Adds space between the text and the badge
  },
}));

// ----------------------------------------------------------------------

NavItem.propTypes = {
  level: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  href: PropTypes.string,
  info: PropTypes.element,
  icon: PropTypes.element,
  open: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  badgeModule: PropTypes.string,
  badgeContent: PropTypes.number
};

function NavItem({
  level,
  title,
  href,
  info,
  icon,
  badgeModule,
  badgeContent,
  open = false,
  children,
  className,
  ...other
}) {
  const classes = useStyles();
  const [show, setShow] = useState(open);
  const isSubItem = level > 0;

  const dispatch = useDispatch()

  const handleShow = () => {
    setShow((show) => !show);
  };

  if (children) {
    return (
      <>
        <ListItem
          button
          disableGutters
          onClick={handleShow}
          className={clsx(
            classes.listItem,
            {
              [classes.isActiveListItem]: open
            },
            className
          )}
          {...other}
        >
          <ListItemIcon>{icon && icon}</ListItemIcon>
          <ListItemText disableTypography primary={title} />
          {info && info}  {badgeContent > 0 && (<Badge classes={{ badge: classes.customBadge }} badgeContent={badgeContent}/>)}
          <Box
            component={Icon}
            icon={show ? arrowIosDownwardFill : arrowIosForwardFill}
            sx={{ width: 16, height: 16, ml: 1 }}
          />
        </ListItem>

        <Collapse in={show}>{children}</Collapse>
      </>
    );
  }

  return (
    <ListItem
      button
      exact={open}
      disableGutters
      {...(title === "user guide" ? {} : { to: href, component: RouterLink })}
      activeClassName={
        isSubItem ? classes.isActiveListItemSub : classes.isActiveListItem
      }
      className={clsx(classes.listItem, className)}
      onClick={async () => {
        if (title === "Generate Catalogue") {
          dispatch(resetGenerateProductCatalogue())
        }

        if (title === "Stock Returnable") {
          dispatch(setIsClickNavbar(true))
        }

        if (title === "offers buyer") {
          localStorage.removeItem("searchQueryOfferSalesManager")
          localStorage.removeItem("searchOfferSalesManager")
          localStorage.removeItem("tabOfferSalesManager")
        }

        if (title === "offers sales") {
          localStorage.removeItem("searchQueryOfferSales")
          localStorage.removeItem("searchOfferSales")
          localStorage.removeItem("tabOfferSales")
        }

        if (title === "offers admin") {
          localStorage.removeItem("searchQueryOfferAdmin")
          localStorage.removeItem("searchOfferAdmin")
          localStorage.removeItem("tabOfferAdmin")
        }

        if (title === "forecast") {
          localStorage.removeItem("searchQueryForecastManager")
          localStorage.removeItem("searchForecastManager")
          localStorage.removeItem("tabForecastManager")
        }

        if (title === "closed offers") {
          localStorage.removeItem("searchQueryClosedOffers")
          localStorage.removeItem("searchClosedOffers")
          localStorage.removeItem("tabClosedOffers")
        }

        if (title === "closed offers buyer") {
          localStorage.removeItem("searchQueryClosedOffersManager")
          localStorage.removeItem("searchClosedOffersManager")
          localStorage.removeItem("tabClosedOffersManager")
        }

        if (title === "pre-order sales") {
          localStorage.removeItem("searchQueryPreOrderSales")
          localStorage.removeItem("searchPreOrderSales")
          localStorage.removeItem("tabPreOrderSales")
        }

        if (title === "pre-order sales manager") {
          localStorage.removeItem("searchQueryPreOrderSalesManager")
          localStorage.removeItem("searchPreOrderSalesManager")
          localStorage.removeItem("tabPreOrderSalesManager")
        }

        if (title === "purchase order buyer") {
          localStorage.removeItem("searchQueryPurchaseOrder")
          localStorage.removeItem("searchPurchaseOrder")
          localStorage.removeItem("tabPurchaseOrder")
        }

        if (title === "sales order") {
          localStorage.removeItem("filterSalesOrderType")
        }

        if (title === "sales order manager") {
          localStorage.removeItem("filterSalesOrderManagerType")
        }

        if (title === "orders finance") {
          localStorage.removeItem("filterOrderFinanceType")
        }

        if (title === "orders logistic") {
          localStorage.removeItem("filterOrderLogisiticType")
        }

        if (title === "orders logistic manager") {
          localStorage.removeItem("filterOrderLogisiticManagerType")
        }

        if (title === "logistic") {
          localStorage.removeItem("filterOrderAdminLogisiticType")
        }

        if (title === "finance") {
          localStorage.removeItem("filterOrderAdminFinanceType")
        }

        if (title === "sales") {
          localStorage.removeItem("filterOrderAdminSalesType")
        }

        if (title === "invoice admin finance") {
          localStorage.removeItem("filterInvoiceAdminFinance")
        }
        if (title === "invoice admin sales") {
          localStorage.removeItem("filterInvoiceAdminSales")
        }
        if (title === "invoices finance") {
          localStorage.removeItem("filterInvoicesFinance")
        }
        if (title === "invoices finance manager") {
          localStorage.removeItem("filterInvoicesFinanceManager")
        }
        if (title === "invoices sales") {
          localStorage.removeItem("filterInvoicesSales")
        }

        if (title === "consignment sales") {
          localStorage.removeItem("filterConsignmentSales")
        }

        if (title === "user guide") {
          //window.open(`${process.env.REACT_APP_STORAGE_URL}/${process.env.REACT_APP_STORAGE_CONTAINER}/Betobee_AdminGuide_v2.0.pdf`, "_blank")
          await openAsset(
            `${process.env.REACT_APP_STORAGE_URL}/${process.env.REACT_APP_STORAGE_CONTAINER}/Betobee_AdminGuide_v2.0.pdf`
          );

          return;
        }
      }}
      isActive={(match, location) => {
        if (!match) {
          return false;
        }
        const { url } = match;
        const { pathname } = location;
        //const isMatch = url === pathname;

        /*if (!isSubItem) {
          return url.length && pathname.includes(url);
        }
        */
        const excludeProductPaths = ['/module/product/generate-product-catalogue'];

        if (!isSubItem) {
          if (url === '/module/product') {
            // Product menu aktif untuk semua sub-route kecuali yang di exclude
            return pathname.startsWith(url) && !excludeProductPaths.some(path => pathname.startsWith(path));
          }

          if (url === '/module/product/generate-product-catalogue') {
            // Generate Catalogue menu aktif hanya untuk exact match atau sub-routes nya
            return pathname.startsWith(url);
          }

          // Menu lain
          return url.length && pathname.includes(url);
        }

        // untuk sub-item pakai exact match
        return url === pathname;
      }}
      {...other}
    >
      <ListItemIcon className={classes.listItemIcon}>
        {isSubItem ? <span className={classes.subIcon} /> : icon}
      </ListItemIcon>

      <div className={classes.infoWrapper}>
        <ListItemText 
          disableTypography 
          primary={title} 
          style={{
            textOverflow: badgeContent > 0 ? 'ellipsis' : 'unset',
            whiteSpace: badgeContent > 0 ? 'nowrap' : 'normal',
            overflow: badgeContent > 0 ? 'hidden' : 'visible',
            display: 'inline-block',
            maxWidth: badgeContent > 0 ? '150px' : 'unset', // Adjust the maxWidth only if badgeContent is present
            flexGrow: 1, // Allows the text to take available space
          }}
        />
        {info && <span className={classes.infoText}>{info}</span>}
        {badgeContent > 0 && (
          <Badge 
            classes={{ badge: classes.customBadge }} 
            badgeContent={badgeContent} 
          />
        )}
      </div>
    
      
    </ListItem>
  );
}

export default NavItem;
